import { Hint, Panel, Toggle } from './ui/Controls';
import { store, useStore } from '@/state/store';
import { hasNativeFaceDetection } from '@/engine/analyzer';

/**
 * Face-protection readout. Being explicit about detection *source* matters:
 * the heuristic finds skin-toned regions, which is useful but not the same as
 * finding faces, and a user placing type over a clinical before/after should
 * know which one is running.
 */
export function FaceDetectorPanel() {
  const analysis = useStore((s) => s.analysis);
  const showFaces = useStore((s) => s.showFaces);
  const showGuides = useStore((s) => s.showGuides);

  return (
    <Panel title="Composition">
      <div className="space-y-1">
        <Toggle
          label="Show safe area"
          checked={showGuides}
          onChange={(v) => store.set({ showGuides: v })}
          hint="Where platform UI can crop or cover the design"
        />
        <Toggle
          label="Show protected areas"
          checked={showFaces}
          onChange={(v) => store.set({ showFaces: v })}
          hint="Regions the layout engine keeps clear"
        />
      </div>

      {analysis && (
        <div className="mt-3 space-y-2 border-t border-white/[0.06] pt-2.5">
          <Row label="Faces detected" value={String(analysis.faces.length)} />
          {analysis.faces.length > 0 && (
            <Row
              label="Detection method"
              value={analysis.faces[0].source === 'native' ? 'Browser API' : 'Skin-tone heuristic'}
            />
          )}
          <Row
            label="Focal point"
            value={`${(analysis.focalPoint.x * 100).toFixed(0)}%, ${(analysis.focalPoint.y * 100).toFixed(0)}%`}
          />
          <Row
            label="Subject box"
            value={`${(analysis.subject.w * 100).toFixed(0)}×${(analysis.subject.h * 100).toFixed(0)}%`}
          />
          <Row label="Average luminance" value={analysis.averageLuminance.toFixed(2)} />
          <Row label="Contrast spread" value={analysis.contrastSpread.toFixed(2)} />

          <div className="pt-1">
            <span className="field-label">Zone scores</span>
            <div className="space-y-1">
              {analysis.zones.slice(0, 5).map((zone) => (
                <div key={zone.id} className="flex items-center gap-2">
                  <span className="w-20 shrink-0 truncate text-[10px] text-ink-400">
                    {zone.label}
                  </span>
                  <span className="h-1 flex-1 overflow-hidden rounded-full bg-white/[0.06]">
                    <span
                      className="block h-full rounded-full bg-accent"
                      style={{ width: `${Math.max(2, Math.min(100, zone.score * 100))}%` }}
                    />
                  </span>
                  <span className="w-8 shrink-0 text-right font-mono text-[9px] text-ink-500">
                    {zone.score.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {analysis.faces.length > 0 && analysis.faces[0].source === 'heuristic' && (
            <Hint>
              This browser has no native face detection, so protection is coming
              from a skin-tone heuristic. It errs toward over-protecting: it can
              flag hands or skin-toned backgrounds as well as faces. Verify
              placement on clinical photography.
            </Hint>
          )}
          {analysis.faces.length === 0 && !hasNativeFaceDetection() && (
            <Hint>
              No faces found by the skin-tone heuristic — this browser has no
              native face detection. If the photo contains a face, check
              placement before exporting.
            </Hint>
          )}
        </div>
      )}
    </Panel>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[10px] text-ink-400">{label}</span>
      <span className="font-mono text-[10px] text-ink-200">{value}</span>
    </div>
  );
}
